import type { GalleryMediaFile } from "@next-platform/client-common-core";
import { useMediaFiles, useOpenViewMediaModal, useUserSettings } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { useCallback } from "react";

import Trans from "@/components/Trans/Trans";
import MediaFilePreview from "@/pages/Gallery/components/MediaFilePreview";
import getShouldBlurNsfwAvatars from "@/utils/getShouldBlurNsfwAvatars";

import GalleryEmptyState from "./GalleryEmptyState";
import GalleryLoadError from "./GalleryLoadError";
import GalleryTileSkeleton from "./GalleryTileSkeleton";
import GalleryTitle from "./GalleryTitle";
import type { GalleryProps } from "./types";
import useDelayedFlag from "./useDelayedFlag";
import useEndOfListSentinel from "./useEndOfListSentinel";

export const GALLERY_TEST_ID = "Gallery";
// Enough placeholders to fill the first rows of the widest grid; a following batch only needs a hint.
// Counts and the delay come from the Gallery user flow: three skeletons while the first batch loads, one
// while infinite scroll fetches the next.
export const FIRST_BATCH_SKELETON_COUNT = 3;
export const NEXT_BATCH_SKELETON_COUNT = 1;
// A fast answer shows no skeletons at all, so a quick load does not flash placeholders at the reader.
export const SKELETON_DELAY_MS = 200;
const FIRST_BATCH_SKELETON_KEYS = Array.from({ length: FIRST_BATCH_SKELETON_COUNT }, (element, position) => `first-${position}`);
const NEXT_BATCH_SKELETON_KEYS = Array.from({ length: NEXT_BATCH_SKELETON_COUNT }, (element, position) => `next-${position}`);
const NO_SKELETON_KEYS: string[] = [];

function getSkeletonKeys(isLoadingFirstPage: boolean, isLoadingMore: boolean): string[] {
  if (isLoadingFirstPage) {
    return FIRST_BATCH_SKELETON_KEYS;
  }
  return isLoadingMore ? NEXT_BATCH_SKELETON_KEYS : NO_SKELETON_KEYS;
}
const GRID_CLASS_NAME = "grid grid-cols-2 gap-3 px-3 pb-6 pt-4 md:grid-cols-3 md:gap-6 md:px-6 lg:grid-cols-4 xl:grid-cols-5";
// content-visibility skips layout and paint for tiles far off screen, which keeps a long grid smooth without a virtualization library.
const TILE_CLASS_NAME = "[content-visibility:auto] [contain-intrinsic-size:auto_360px]";

const Gallery = ({ title, source, pageSize, "data-testid": testId = GALLERY_TEST_ID, onOpenFile }: GalleryProps) => {
  const { files, isLoadingFirstPage, isRefreshing, isLoadingMore, hasMore, isEmpty, error, loadNextPage, retry } = useMediaFiles(source, {
    pageSize,
  });

  // A failed batch must not retry itself every time the sentinel scrolls into view; the retry control is the user's call.
  const isPaginationEnabled = hasMore && !isLoadingFirstPage && !isRefreshing && !isLoadingMore && !error;
  const refSentinel = useEndOfListSentinel({ enabled: isPaginationEnabled, onEndReached: loadNextPage });

  // Skeletons wait out a brief load, so a fast answer goes straight to tiles.
  const isFirstPageSlow = useDelayedFlag(isLoadingFirstPage, SKELETON_DELAY_MS);
  const isNextPageSlow = useDelayedFlag(isLoadingMore, SKELETON_DELAY_MS);
  const skeletonKeys = getSkeletonKeys(isFirstPageSlow, isNextPageSlow);
  // A retry hides the block the moment it starts, not when its skeletons appear, so the delay never
  // flashes the failure back at the reader.
  const isLoadErrorVisible = error !== null && !isLoadingFirstPage && !isLoadingMore;

  // The grid holds the order the user sees, so it hands the modal that list to step through. A page
  // passing `onOpenFile` keeps its own behaviour instead.
  const open = useOpenViewMediaModal();
  // The modal is a capability of its own: a brand without it gets tiles that open nothing.
  const isOpenDetailsAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.GalleryOpenMediaDetails);
  // The badge says a file is adult; whether to blur it is the reader's own setting, read once for the grid.
  const { blurNSFW, showNSFW } = useUserSettings();
  const isNsfwUserToggleAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.NsfwUserToggle);
  // Two settings hide adult content: "show NSFW" off means never show it uncovered, and "blur NSFW" is the
  // softer preference for a reader who does see it. Turning the first off clears the second, so both are read.
  const shouldBlurNsfw = !showNSFW || getShouldBlurNsfwAvatars({ isNsfwUserToggleAllowed, blurNSFW });
  const handleOpenFile = useCallback(
    (file: GalleryMediaFile) => {
      if (onOpenFile) {
        onOpenFile(file);
        return;
      }
      open(
        file.id,
        files.map((listed) => listed.id),
      );
    },
    [onOpenFile, open, files],
  );

  // One flag for "a request is running": the title spinner and the section's busy state must agree.
  const isLoading = isLoadingFirstPage || isRefreshing || isLoadingMore;

  return (
    <section data-testid={testId} aria-busy={isLoading}>
      <GalleryTitle title={title} isLoading={isLoading} delayMs={SKELETON_DELAY_MS} testId={`${testId}-Title`} />
      <div data-testid={`${testId}-Grid`} className={GRID_CLASS_NAME}>
        {files.map((file) => (
          <div key={file.id} className={TILE_CLASS_NAME}>
            <MediaFilePreview
              file={file}
              isBlurred={shouldBlurNsfw}
              data-testid={`MediaFilePreview-${file.id}`}
              onOpen={onOpenFile || isOpenDetailsAllowed ? handleOpenFile : undefined}
            />
          </div>
        ))}
        {skeletonKeys.map((skeletonKey) => (
          <GalleryTileSkeleton key={skeletonKey} testId={`${testId}-Skeleton`} />
        ))}
        {isEmpty ? <GalleryEmptyState testId={`${testId}-EmptyState`} /> : null}
      </div>
      {skeletonKeys.length > 0 ? (
        <span role="status" className="sr-only">
          <Trans i18nKey="gallery:grid.loading" />
        </span>
      ) : null}
      {isLoadErrorVisible ? <GalleryLoadError testId={`${testId}-LoadError`} onRetry={retry} /> : null}
      {hasMore ? <div ref={refSentinel} aria-hidden="true" data-testid={`${testId}-Sentinel`} className="h-px w-full" /> : null}
    </section>
  );
};

export default Gallery;
